import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

// Builds the setup steps for a given role and current account state.
// Each step: key, label, sub, action path, done (auto-detected), optional flag.
const buildSteps = (role, state) => {
  const { hasHorses, hasOrg, hasContacts, hasMembers, hasDocs, profileComplete } = state;

  const ownerSteps = [
    { key: 'add_horse', icon: '🐴', label: 'Add your horses', sub: 'Create a profile for each horse', path: '/horses/add', done: hasHorses },
    { key: 'care_team', icon: '🩺', label: 'Add your care team', sub: 'Your vet, farrier & others', path: '/contacts', done: hasContacts },
  ];

  if (role === 'barn_owner') {
    return [
      { key: 'create_barn', icon: '🏠', label: 'Set up your barn', sub: 'Create your barn to manage members', path: '/organizations', done: hasOrg },
      { key: 'barn_profile', icon: '📝', label: 'Complete your barn profile', sub: 'Location, phone & disciplines', path: '/organizations', done: hasOrg && profileComplete },
      { key: 'invite_members', icon: '✉️', label: 'Invite your members', sub: 'Share your invite link or code', path: '/organizations', done: hasMembers },
      { key: 'barn_directory', icon: '🩺', label: "Add your barn's vet & farrier", sub: 'Build the shared directory', path: '/contacts', done: hasContacts },
      { key: 'barn_docs', icon: '📄', label: 'Upload documents for members to sign', sub: 'Liability release, agreements (optional)', path: '/organizations', done: hasDocs, optional: true },
      { key: 'add_horse', icon: '🐴', label: 'Add your own horses', sub: 'You can manage your horses too', path: '/horses/add', done: hasHorses },
    ];
  }

  if (role === 'trainer') {
    return [
      { key: 'create_business', icon: '💼', label: 'Set up your training business', sub: 'Create your business to manage clients', path: '/organizations', done: hasOrg },
      { key: 'business_profile', icon: '📝', label: 'Complete your business profile', sub: 'Location, phone & disciplines', path: '/organizations', done: hasOrg && profileComplete },
      { key: 'invite_clients', icon: '✉️', label: 'Add your first client', sub: 'Share your invite link or code', path: '/organizations', done: hasMembers },
      { key: 'trainer_docs', icon: '📄', label: 'Upload documents for clients to sign', sub: 'Training agreements (optional)', path: '/organizations', done: hasDocs, optional: true },
      { key: 'add_horse', icon: '🐴', label: 'Add your own horses', sub: 'You can manage your horses too', path: '/horses/add', done: hasHorses },
    ];
  }

  if (role === 'barn_trainer') {
    return [
      { key: 'join_barn', icon: '🏇', label: 'Join your barn', sub: 'Enter the invite code your barn gave you', path: '/organizations', done: hasOrg },
      ...ownerSteps,
    ];
  }

  // default: owner
  return ownerSteps;
};

const SetupChecklist = ({ state }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(user?.setup_dismissed || []);

  const role = user?.primary_role || 'owner';
  const steps = buildSteps(role, state).filter(s => !dismissed.includes(s.key));
  const visible = steps.filter(s => !s.done);

  // If everything is done or dismissed, render nothing
  if (visible.length === 0) return null;

  const handleDismiss = async (key, e) => {
    e.stopPropagation();
    const next = [...dismissed, key];
    setDismissed(next);
    try { await api.put('/auth/setup/dismiss', { key }); } catch {}
  };

  const roleTitle = {
    barn_owner: "Let's set up your barn",
    trainer: "Let's set up your business",
    barn_trainer: 'Get connected to your barn',
    owner: 'Get started',
  }[role];

  return (
    <div className="card" style={{ border: '1px solid var(--cr-primary)', background: 'var(--cr-primary-light)' }}>
      <h2 style={{ fontSize: 15, marginBottom: 4 }}>{roleTitle}</h2>
      <p style={{ fontSize: 12, marginBottom: 12 }}>A few steps to get going. Tap any to start, or dismiss what you don't need.</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {visible.map(step => (
          <div key={step.key} onClick={() => navigate(step.path)}
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px', background: 'white', borderRadius: 'var(--radius-md)', cursor: 'pointer' }}>
            <span style={{ fontSize: 22 }}>{step.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                {step.label}{step.optional ? <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)' }}> · optional</span> : ''}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{step.sub}</div>
            </div>
            <button onClick={(e) => handleDismiss(step.key, e)}
              style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 18, padding: '4px 8px' }}
              aria-label="Dismiss">×</button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SetupChecklist;
